import { VariantSelectorData } from './QuickAddVariantSelector';
import { getLocationInfo } from '../../../../../utils/location';
import { safeUpdateHtmlNodeAttributes } from '../../../../../utils/dom';
import { contentLoaded } from '../../../../../utils/document-promises';
import { downcaseObjectKeys, tryCatchWrapper } from '../../../../../utils/helpers';
import { ShippingCountryState } from '../../../../../utils/shipping';
import {
  GetTheLookExtendedProductQuery,
  GetTheLookExtendedProductQueryVariables,
  GetTheLookProductFragment,
  PlpProductsExtendedQuery,
  PlpProductsExtendedQueryVariables,
} from 'src/generated/shopify-graphql';
import { getIdFromShopifyGraphQLId } from 'src/scripts/utils/shopify';
import { loader } from 'graphql.macro';
import { CartApolloClient } from 'src/scripts/utils/cart/client';
import { notNil } from 'src/scripts/utils/typescriptUtils';
import { ISizesData } from './UpsellProductCards';
import { CartVariantData } from 'src/scripts/utils/cart';

const client = CartApolloClient;

const extendedProductQuery = loader(
  './../../../../../templates/product-listing-page/quick-add/GetExtendedProduct.shopify.graphql'
);

const getTheLookExtendedQuery = loader(
  './../../../../../templates/product/components/GetTheLook/GetTheLookExtendedProduct.shopify.graphql'
);

// Cart Impulse
export const OPEN_VARIANT_SELECTOR_DROPDOWN = 'OPEN VARIANT SELECTOR DROPDOWN';
export const dispatchOpenVariantSelectorDropdown = (data: VariantSelectorData) => {
  document.dispatchEvent(new CustomEvent(OPEN_VARIANT_SELECTOR_DROPDOWN, { detail: data }));
};

export const ADDING_TO_CART = 'ADDING TO CART STATE';
export const dispatchAddingToCart = (data: HTMLElement | undefined) => {
  document.dispatchEvent(new CustomEvent(ADDING_TO_CART, { detail: data }));
};

export const {
  menUpsellCollection: menCollection,
  womenUpsellCollection: womenCollection,
  accessoriesUpsellCollection: accessoriesCollection,
  cartUpsellBlockTitle: blockTitle,
}: {
  menUpsellCollection: string | undefined;
  womenUpsellCollection: string | undefined;
  accessoriesUpsellCollection: string | undefined;
  cartUpsellBlockTitle: string;
  // XXX: Add types for window.
  // @ts-expect-error
} = window.getShopifySettingsCart();

export const categoryToCollectionHandle: Record<string, string | undefined> = {
  accessories: accessoriesCollection,
  women: womenCollection,
  men: menCollection,
};

const serverCartData: WindowExtended.ServerCartData | undefined = window.server_cart_data;
export const shouldRenderUpsellCarousel =
  serverCartData?.settings.enable_cart_upselling_block && (menCollection || womenCollection || accessoriesCollection);

// [REVISIT] as a team we should decide if we want to use 'globals' from the window or w/ 'import' syntax
// Should last after all pending dom mutations/additions (related to 'free shipping messages')
export const updateCartShippingMessagesWithGeolocation = async () => {
  const locationInfo = await getLocationInfo();
  await contentLoaded;

  const { country_code: countryCodefromGEO } = locationInfo;
  let { countryCode, country } = ShippingCountryState;
  const currentCountry = countryCode || countryCodefromGEO;
  const { shippingType } = country || {};

  let returnsHeader: string | undefined;
  const { customReturnsMessagingEnabled, returnsMessaging: unsanitizedReturnMessaging } = window?.getSiteSettings();
  const returnsMessaging = downcaseObjectKeys(unsanitizedReturnMessaging) as ReturnsMessagingJSON;
  if (customReturnsMessagingEnabled) {
    returnsHeader = returnsMessaging[currentCountry.toLowerCase()]?.cart || returnsMessaging['default'].cart;
  } else {
    returnsHeader = shippingType === 'US' ? 'Easy Returns' : 'Returns';
  }

  const cartDefaultReturnsCopy = window.getSiteSettings()?.cartDefaultReturnsCopy;
  const cartDomesticReturnsCopy = window.getSiteSettings()?.cartDomesticReturnsCopy;

  // Cart Returns heading & body
  tryCatchWrapper(() => {
    safeUpdateHtmlNodeAttributes('#cart-sidebar-returns-heading', {
      innerHTML: returnsHeader,
    });
    safeUpdateHtmlNodeAttributes('#cart-sidebar-returns-copy', {
      innerHTML: shippingType === 'US' ? cartDomesticReturnsCopy || '' : cartDefaultReturnsCopy || '',
    });
  });
};

export const fetchExtendedProductData = async ({ handle }: { handle: string }) => {
  try {
    const resp = await client.query<PlpProductsExtendedQuery, PlpProductsExtendedQueryVariables>({
      query: extendedProductQuery,
      variables: { handle },
    });
    const productData = resp.data?.productByHandle;
    const sizesData = productData?.variants.edges.map((edge) => ({
      isSizeAvailable: edge.node.availableForSale,
      quantityAvailable: edge.node.quantityAvailable,
      value: edge.node.selectedOptions.find((item) => item.name === 'Size')?.value,
      variantId: getIdFromShopifyGraphQLId(edge.node.id),
      sku: edge.node.sku,
      price: edge.node.priceV2,
      compareAtPrice: edge.node.compareAtPriceV2,
    }));

    if (!productData || !sizesData?.length) {
      throw new Error('Bad Shopify product: no variants found');
    }

    const singleVariant = sizesData[0];
    const productId = getIdFromShopifyGraphQLId(productData.id);
    const variantPricesData = {
      id: Number(singleVariant?.variantId),
      price: Number(singleVariant?.price.amount),
      displayPrice: Number(singleVariant?.compareAtPrice),
      tags: productData.tags,
      productId: Number(productId),
    };

    const isAtLeastOneSizeAvailable = sizesData.some((size) => size.isSizeAvailable);
    return {
      productData,
      sizesData,
      singleVariant,
      variantPricesData,
      isAtLeastOneSizeAvailable,
    };
  } catch (err) {
    console.error('Failed extendedProductData:', err);
  }
};

export const fetchGetTheLookExtendedData = async ({ ids }: { ids: string[] }): Promise<GetTheLookProductFragment[]> => {
  const variables = {
    ids: ids,
  };
  const resp = await client.query<GetTheLookExtendedProductQuery, GetTheLookExtendedProductQueryVariables>({
    query: getTheLookExtendedQuery,
    variables: variables,
  });
  return resp.data.nodes
    .map((node) => {
      if (node?.__typename !== 'Product') return null;
      return node.onlineStoreUrl ? node : null;
    })
    .filter(notNil);
};

export interface ExtendedProductData {
  productData: PlpProductsExtendedQuery['productByHandle'];
  sizesData: ISizesData[];
  singleVariant: ISizesData | undefined;
  variantPricesData: CartVariantData;
  isAtLeastOneSizeAvailable: boolean;
}
